import * as React from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { SemanticReactHookFormsInput } from "@teamprevent/common/forms/SemanticReactHookFormsInput";
import { SemanticReactHookFormsWeekDaysSelector } from "@teamprevent/common/forms/SemanticReactHookFormsWeekDaysSelector";

import { CREATE_ROOM, SAVE_ROOM } from "../actiontypes";
import { RoomCreateUpdateModel, roomCreateUpdateModelSchemaFactory } from "../model";

interface CreateEditModalProps {
    close: () => void;
    defaultValues: RoomCreateUpdateModel;
    isSavingRequestName: typeof CREATE_ROOM | typeof SAVE_ROOM;
    open: boolean;
    save: (room: RoomCreateUpdateModel) => Promise<void>;
    modalTitle: string;
}

export const CreateEditModal: React.FC<CreateEditModalProps> = ({ close, defaultValues, isSavingRequestName, modalTitle, open, save }) => {
    const { control, handleSubmit } = useForm<RoomCreateUpdateModel>({
        resolver: zodResolver(roomCreateUpdateModelSchemaFactory()),
        defaultValues
    });

    const saveRoom = React.useCallback(async (model: RoomCreateUpdateModel) => {
        await save(model);
    }, [save]);

    const submit = React.useMemo(() => handleSubmit(saveRoom), [handleSubmit, saveRoom]);

    return (
        <Modal as={Form} size="small" open={open} onClose={close} closeOnDimmerClick={false} onSubmit={submit}>
            <Modal.Header>{modalTitle}</Modal.Header>
            <Modal.Content>
                <SemanticReactHookFormsInput
                    name="name"
                    label="Name"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="addressLine1"
                    label="Address Line 1"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="addressLine2"
                    label="Address Line 2"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="addressLine3"
                    label="Address Line 3"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="city"
                    label="City"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="county"
                    label="County"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="postcode"
                    label="Postcode"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="notes"
                    label="Internal Notes"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="customerNotes"
                    label="Customer Notes"
                    control={control}
                />
                <SemanticReactHookFormsInput
                    name="workingDayStartTime"
                    label="Working Day Start Time"
                    control={control}
                    type="time"
                />
                <SemanticReactHookFormsInput
                    name="workingDayEndTime"
                    label="Working Day Finish Time"
                    control={control}
                    type="time"
                />
                <SemanticReactHookFormsWeekDaysSelector
                    name="workingDays"
                    label="Working Week"
                    control={control} />
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={close}>Cancel</Button>
                <IsLoadingButton requestNames={isSavingRequestName} content="Save" type="submit" />
            </Modal.Actions>
        </Modal>
    );
};
